import * as React from 'react'
import { v4 as uuid } from 'uuid'

import {
  EnterpriseContext,
  getSafeUrlParametersFromWindow,
  useLogger,
  useSessionSettings,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { useCustomer } from '@thg-commerce/enterprise-core/src/CustomerContext/hooks'
import { getCustomerLoginState } from '@thg-commerce/enterprise-core/src/utils/customerLoginState'
import { BasketData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'

import { getCartObject } from './cart'
import { getDeviceObject } from './device'
import { getErrorsList } from './errors'
import { BaseEventType, getEventObject } from './event'
import { getOriginObject } from './origin'
import { getPropertyObject } from './property'
import { getRayIds } from './ray_ids'
import { getRequestObject } from './request'

export type { Cart, CartItem, Product } from './types'

export interface BackendMetricArguments<EventType> {
  eventData: EventType
  requestData: {
    start_timestamp: number
    url: string
    duration_ms: number
  }
  errors?: { type: string; label: string }[]
  rays?: String[]
  experiments?: { [key: string]: string }
  basket?: BasketData
  customerData?: {
    receive_newsletter?: boolean | null
  }
}

export const useBackendEventNormaliser = () => {
  const siteConfig = useSiteConfig()
  const logger = useLogger()
  const {
    requestConfig,
    metricNonce,
    extensionsRef,
    history,
  } = React.useContext(EnterpriseContext)
  const customer = useCustomer()

  const {
    value: [getExtensions],
  } = extensionsRef

  const { defaultLocale, subsite, siteId, channel } = useSiteDefinition()
  const sessionSettings = useSessionSettings()

  const normaliseBackendEvent = <EventType extends BaseEventType>(
    argObject: BackendMetricArguments<EventType>,
  ) => {
    const urlParams = getSafeUrlParametersFromWindow(window, '*')
    const referrer =
      history.current && history.current.length >= 2
        ? history.current[history.current.length - 2]
        : document.referrer

    const extensions = getExtensions()
    const backendEvent = {
      cart: argObject.basket
        ? getCartObject(argObject.basket, sessionSettings.currency)
        : null,
      insert_id: uuid(),
      nonce: metricNonce,
      event: getEventObject(argObject.eventData),
      request: getRequestObject(argObject.requestData),
      property: getPropertyObject({
        defaultLocale,
        subsite,
        siteId,
        channel,
        customerLocation:
          requestConfig.customerLocation === 'unknown'
            ? null
            : requestConfig.customerLocation,
      }),
      device: getDeviceObject(siteConfig, requestConfig.customerLocation),
      origin: getOriginObject({ urlParams, referrer }),
      experiments: [],
      ray_ids: getRayIds(argObject.rays ?? []),
      customer: {
        login_state: getCustomerLoginState(extensions?.flags ?? []),
        receive_newsletter:
          argObject.customerData?.receive_newsletter !== undefined
            ? argObject.customerData?.receive_newsletter
            : customer?.emailMarketingPreference || false,
      },
    }

    if (typeof backendEvent.request.duration_ms !== 'number') {
      logger.debug(
        'Invalid device object - duration_ms should be of type number.',
        { url: backendEvent.request.url },
      )
    }

    if (argObject.eventData.type === 'page_visit') {
      return backendEvent
    }
    backendEvent['errors'] = getErrorsList(
      argObject.errors,
      argObject.eventData,
    )
    return backendEvent
  }

  return normaliseBackendEvent
}
